import { animate, style, transition, trigger } from "@angular/animations";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { combineLatest, debounceTime, first, Subject, takeUntil, timer } from "rxjs";
import { IBiobank } from "../../interfaces/biobanks.interface";
import { ISkippedAndDuplicateSamples } from "../../interfaces/rna-mutations.interface";
import { BiobankService } from "../../services/biobank.service";
import { RnaMutationsService } from "../../services/rna-mutations.service";

@Component({
  selector: "app-rna-mutation-upload",
  templateUrl: "./rna-mutation-upload.component.html",
  styleUrls: ["./rna-mutation-upload.component.scss"],
  animations: [
    trigger("fadeInOut", [
      transition(":enter", [style({ opacity: 0 }), animate("600ms ease-in", style({ opacity: 1 }))]),
      transition(":leave", [animate("600ms ease-out", style({ opacity: 0 }))]),
    ]),
  ],
})
export class RnaMutationUploadComponent implements OnInit, OnDestroy {
  @ViewChild("rnaInput") rnaInput!: ElementRef<HTMLInputElement>;
  @ViewChild("mutationInput") mutationInput!: ElementRef<HTMLInputElement>;

  @Input() isModal = false;
  @Input() initialBiobankName?: string;
  @Input() initialSampleId?: string;

  @Output() messagesChanged = new EventEmitter<string[]>();
  @Output() uploadingChange = new EventEmitter<boolean>();
  @Output() uploadSuccess = new EventEmitter<void>();

  biobanks: IBiobank[] = [];
  selectedBiobankId: number | null = null;
  sampleId = "";

  isUploading = false;
  isLoadingRNAMut = false;
  dots = Array(5);
  messages: string[] = [];
  overviewData: any = null;

  private onDestroy$ = new Subject<void>();
  private sampleIdChanged$ = new Subject<void>();

  constructor(
    private biobankService: BiobankService,
    private rnaMutationsService: RnaMutationsService,
    private translateService: TranslateService
  ) {}

  /**
   * Loads the available biobanks, preselects the initial biobank/sample if provided, and
   * sets up the debounced sample id overview refresh.
   */
  ngOnInit(): void {
    if (this.initialSampleId) {
      this.sampleId = this.initialSampleId;
    }

    this.biobankService
      .getBiobanks()
      .pipe(first())
      .subscribe({
        next: (data: IBiobank[]) => {
          this.biobanks = data;

          if (this.initialBiobankName) {
            const parsed = this.initialBiobankName.match(/^(.*)\s\((.*)\)$/);

            if (!parsed) {
              throw new Error(`Invalid biobank format: ${this.initialBiobankName}`);
            }

            const [, name, acronym] = parsed;

            const match = this.biobanks.find((b) => b.name === name && b.acronym === acronym);

            if (match) {
              this.selectedBiobankId = match.id;
              if (this.sampleId) {
                this.fetchOverview();
              }
            } else {
              const message = this.translateService.instant("biobank.loadError");
              alert(message);
            }
          }
        },
        error: () => {
          // TODO
          const message = this.translateService.instant("biobank.loadError");
          alert(message);
        },
      });

    this.sampleIdChanged$.pipe(debounceTime(2000), takeUntil(this.onDestroy$)).subscribe(() => this.fetchOverview());
  }

  /**
   * Completes all active subscriptions when the component is destroyed.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Clears messages and triggers a debounced overview refresh when the selected biobank changes.
   */
  onBiobankChange(): void {
    this.messages = [];
    this.emitMessages();
    this.sampleIdChanged$.next();
  }

  /**
   * Clears messages and triggers a debounced overview refresh when the sample id changes.
   */
  onSampleIdChange(): void {
    this.messages = [];
    this.emitMessages();
    this.sampleIdChanged$.next();
  }

  /**
   * Emits the current messages list to parent components.
   */
  private emitMessages(): void {
    this.messagesChanged.emit(this.messages);
  }

  /**
   * Fetches the RNA/mutation overview for the currently selected biobank and sample id,
   * enforcing a minimum loading delay so the UI does not flicker.
   */
  fetchOverview(): void {
    this.isLoadingRNAMut = true;

    if (!this.selectedBiobankId || !this.sampleId) {
      this.overviewData = null;
      this.isLoadingRNAMut = false;
      return;
    }

    const delay = 500;
    const loadingDelay$ = timer(delay);
    const mutationRequest$ = this.rnaMutationsService.getHasRnaMutation(this.selectedBiobankId, this.sampleId);
    // The idea is that the processing time of fetchOverview does not exceed the one that took
    // the longest time between the delay and the getHasRnaMutation.
    combineLatest([mutationRequest$, loadingDelay$])
      .pipe(first())
      .subscribe({
        next: ([data]: any) => {
          const sampleID = `${this.selectedBiobankId}_${this.sampleId}`;

          if (typeof data === "string") {
            // Case 2: Error message in string form
            this.overviewData = {
              sampleID,
              hasRnaSeq: undefined,
              hasMutation: undefined,
              errorMessage: data,
            };
          } else if (typeof data === "object" && data !== null) {
            // Case 1: Object with rnaSeq and mutations keys
            this.overviewData = {
              sampleID,
              hasRnaSeq: data.rnaSeq ?? undefined,
              hasMutation: data.mutations ?? undefined,
            };
          } else {
            // Case 3: Unexpected response
            this.overviewData = {
              sampleID,
              hasRnaSeq: undefined,
              hasMutation: undefined,
              errorMessage: "Unexpected response format",
            };
          }

          this.isLoadingRNAMut = false;
        },
        error: () => {
          // Case 4: HTTP or network error
          this.overviewData = {
            sampleID: `${this.selectedBiobankId}_${this.sampleId}`,
            hasRnaSeq: undefined,
            hasMutation: undefined,
            errorMessage: "Server error or connection issue",
          };
          this.isLoadingRNAMut = false;
        },
      });
  }

  // -------- file triggers --------

  /**
   * Resets the RNA file input and programmatically opens the file picker.
   */
  triggerRNAFileInput(): void {
    this.rnaInput.nativeElement.value = "";
    setTimeout(() => this.rnaInput.nativeElement.click(), 0);
  }

  /**
   * Resets the mutation file input and programmatically opens the file picker.
   */
  triggerMutFileInput(): void {
    this.mutationInput.nativeElement.value = "";
    setTimeout(() => this.mutationInput.nativeElement.click(), 0);
  }

  /**
   * Handles the RNA file input's change event and processes the selected file.
   * @param event Change event from the RNA file input
   */
  handleRNAFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    this.processRNAFile(file);
  }

  /**
   * Handles the mutation file input's change event and processes the selected file.
   * @param event Change event from the mutation file input
   */
  handleMutFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    this.processMutFile(file);
  }

  /**
   * Prevents the browser's default handling so the drop zone can accept dragged files.
   * @param event Drag event
   */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * Prevents the browser's default handling when a dragged file leaves the drop zone.
   * @param event Drag event
   */
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * Processes an RNA file dropped onto the drop zone, ignoring the drop if no biobank is selected.
   * @param event Drop event carrying the dragged file
   */
  onDropRNA(event: DragEvent): void {
    if (!this.selectedBiobankId) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const file = event.dataTransfer?.files?.[0];
    if (file) this.processRNAFile(file);
  }

  /**
   * Processes a mutation file dropped onto the drop zone, ignoring the drop if no biobank is selected.
   * @param event Drop event carrying the dragged file
   */
  onDropMut(event: DragEvent): void {
    if (!this.selectedBiobankId) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const file = event.dataTransfer?.files?.[0];
    if (file) this.processMutFile(file);
  }

  /**
   * Validates and uploads an RNA file for the selected biobank/sample, then updates the
   * status messages (unrecognized samples, duplicated genes, genes skipped for a non-float expression value)
   * and refreshes the overview.
   * @param file RNA file to upload
   */
  private processRNAFile(file: File): void {
    if (!this.selectedBiobankId) return;

    const extension = file.name.split(".").pop()?.toLowerCase();
    if (!["csv", "tsv"].includes(extension || "")) {
      const message = this.translateService.instant("uploadData.msg.invalidFormat");
      alert(message);
      return;
    }

    this.messages = [];
    this.emitMessages();

    const sampleID = this.sampleId?.trim();
    const formData = new FormData();
    formData.append("file", file);

    this.isUploading = true;
    this.uploadingChange.emit(true);

    this.rnaMutationsService
      .uploadRNA(formData, this.selectedBiobankId, sampleID)
      .pipe(first())
      .subscribe({
        next: (response: ISkippedAndDuplicateSamples) => {
          const { skip, duplicates, invalidExpressions } = response;

          if (skip?.length) {
            this.messages.push(this.translateService.instant("uploadData.msg.unrecognized") + " : " + skip.join(", "));
          }

          if (duplicates?.length) {
            duplicates.forEach((dup: { sampleID: string; duplicatedGenes: string[] }) => {
              const genes = dup.duplicatedGenes.join(", ");
              this.messages.push(this.translateService.instant("uploadData.msg.duplicate") + " " + dup.sampleID + " : " + genes);
            });
          }

          if (invalidExpressions?.length) {
            invalidExpressions.forEach((invalid: { sampleID: string; duplicatedGenes: string[] }) => {
              const genes = invalid.duplicatedGenes.join(", ");
              this.messages.push(
                this.translateService.instant("uploadData.rnaMutation.rna.msg.invalidExpression") + " " + invalid.sampleID + " : " + genes
              );
            });
          }

          if (!skip?.length && !duplicates?.length && !invalidExpressions?.length) {
            this.messages.push(this.translateService.instant("uploadData.rnaMutation.rna.msg.ok"));
          }

          this.sampleIdChanged$.next();
          this.isUploading = false;
          this.uploadingChange.emit(false);
          this.uploadSuccess.emit();
          this.emitMessages();
        },
        error: (err: HttpErrorResponse) => {
          const rawMessage = typeof err.error === "string" ? err.error : "Erreur inconnue";

          this.messages.push(this.translateService.instant("uploadData.msg.ko") + " : " + rawMessage);
          this.emitMessages();
          this.isUploading = false;
          this.uploadingChange.emit(false);
        },
      });
  }

  /**
   * Validates and uploads a mutation file for the selected biobank, then updates the
   * status messages and refreshes the overview.
   * @param file Mutation file to upload
   */
  private processMutFile(file: File): void {
    if (!this.selectedBiobankId) return;

    const extension = file.name.split(".").pop()?.toLowerCase();
    if (!["csv", "tsv"].includes(extension || "")) {
      const message = this.translateService.instant("uploadData.msg.invalidFormat");
      alert(message);
      return;
    }

    this.messages = [];
    this.emitMessages();

    const formData = new FormData();
    formData.append("file", file);

    this.isUploading = true;
    this.uploadingChange.emit(true);

    this.rnaMutationsService
      .uploadMutation(formData, this.selectedBiobankId)
      .pipe(first())
      .subscribe({
        next: (response: any) => {
          const { skip, duplicates } = response;

          if (skip?.length) {
            this.messages.push(this.translateService.instant("uploadData.msg.unrecognized") + " : " + skip.join(", "));
          }

          if (duplicates?.length) {
            duplicates.forEach((dup: { sampleID: string; duplicatedGenes: string[] }) => {
              const genes = dup.duplicatedGenes.join(", ");
              this.messages.push(this.translateService.instant("uploadData.msg.duplicate") + " " + dup.sampleID + " : " + genes);
            });
          }

          if (!skip?.length && !duplicates?.length) {
            this.messages.push(this.translateService.instant("uploadData.rnaMutation.mutation.msg.ok"));
          }

          this.sampleIdChanged$.next();
          this.isUploading = false;
          this.uploadingChange.emit(false);
          this.uploadSuccess.emit();
          this.emitMessages();
        },
        error: (err: HttpErrorResponse) => {
          const rawMessage = typeof err.error === "string" ? err.error : "Erreur inconnue";

          this.messages.push(this.translateService.instant("uploadData.msg.ko") + " : " + rawMessage);
          this.emitMessages();
          this.isUploading = false;
          this.uploadingChange.emit(false);
        },
      });
  }
}
